// -- About Types --
export const defaultAboutHero: AboutHeroData = { badge: "", title: "", subtitle: "", description: "", typewriterWords: [], ctaPrimary: "", ctaSecondary: "", image: "" };
export const defaultAboutStory: AboutStoryData = { badge: "", title: "", paragraphs: [], tags: [], image: "", foundedYear: "", teamCount: "" };

export interface AboutHeroData {
  badge: string;
  badgeEn?: string;
  title: string;
  titleEn?: string;
  subtitle: string;
  subtitleEn?: string;
  description: string;
  descriptionEn?: string;
  typewriterWords: string[];
  typewriterWordsEn?: string[];
  ctaPrimary: string;
  ctaPrimaryEn?: string;
  ctaSecondary: string;
  ctaSecondaryEn?: string;
  image: string;
}

export interface AboutStoryData {
  badge: string;
  badgeEn?: string;
  title: string;
  titleEn?: string;
  paragraphs: string[];
  paragraphsEn?: string[];
  tags: string[];
  tagsEn?: string[];
  image: string;
  foundedYear: string;
  teamCount: string;
}

export interface AboutMission {
  id: string;
  title: string;
  titleEn?: string;
  body: string;
  bodyEn?: string;
  dark: boolean;
}

export interface AboutValue {
  id: string;
  icon: string;
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
}

export interface AboutTimelineEvent {
  id: string;
  year: string;
  title: string;
  titleEn?: string;
  desc: string;
  descEn?: string;
}

export interface AboutTeamMember {
  id: string;
  name: string;
  nameEn?: string;
  role: string;
  roleEn?: string;
  experience: string;
  experienceEn?: string;
  image: string;
}

export interface AboutAward {
  id: string;
  icon: string;
  title: string;
  titleEn?: string;
  body: string;
  bodyEn?: string;
}

// -- Offers Types --
export const defaultOffersHero: OffersHeroData = { title: "", subtitle: "", description: "", tags: [], image: "", badge: "", maxDiscount: 0 };
export const defaultOffersNotify: OffersNotifyData = { title: "", description: "", placeholder: "", buttonText: "", subtitle: "", ctaPrimary: { text: "", link: "" }, ctaSecondary: { text: "", link: "" } };

export interface OffersHeroData {
  title: string;
  titleEn?: string;
  subtitle: string;
  subtitleEn?: string;
  description: string;
  descriptionEn?: string;
  tags: string[];
  tagsEn?: string[];
  image: string;
  badge?: string;
  badgeEn?: string;
  maxDiscount?: number;
}

export interface SeasonalOffer {
  id: string;
  title: string;
  titleEn?: string;
  subtitle: string;
  subtitleEn?: string;
  badge: string;
  badgeEn?: string;
  icon: string;
  bgGradient: string;
  badgeColor: string;
  discountPercent: number;
  endDate: string;
  packageIds: string[];
  description: string;
  descriptionEn?: string;
  active?: boolean;
}

export interface FlashDeal {
  id: string;
  packageIds: string[];
  flashDiscount: number;
  endsIn: number;
  label: string;
  labelEn?: string;
  packageId?: string; // fallback
  active?: boolean;
}

export interface HowToRedeemStep {
  id: string;
  step: number | string;
  title: string;
  titleEn?: string;
  description?: string;
  descriptionEn?: string;
  icon: string;
  desc?: string; // fallback
  descEn?: string;
}

export interface OffersNotifyData {
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  placeholder: string;
  placeholderEn?: string;
  buttonText: string;
  buttonTextEn?: string;
  subtitle?: string;
  subtitleEn?: string;
  ctaPrimary?: {
    text: string;
    link: string;
  };
  ctaSecondary?: {
    text: string;
    link: string;
  };
}

// -- Contact Types --
export const defaultContactHero: ContactHeroData = { 
  title: "", 
  description: "", 
  badge: "", 
  typewriterWords: [], 
  backgroundImage: "" 
};
export const defaultContactForm: ContactFormConfig = { 
  title: "", 
  subtitle: "", 
  topics: [], 
  submitText: "", 
  successMessage: "",
  successTitle: "",
  subjects: [],
  fields: {
    name: { label: "", placeholder: "", required: true },
    phone: { label: "", placeholder: "", required: true },
    email: { label: "", placeholder: "", required: false },
    subject: { label: "", required: true },
    message: { label: "", placeholder: "", required: true, maxLength: 500 }
  }
};
export const defaultContactMap: ContactMapConfig = { 
  title: "", 
  address: "", 
  coordinates: { lat: 0, lng: 0 }, 
  embedUrl: "",
  height: 240,
  mapsLink: "",
  mapsLinkText: ""
};
export const defaultContactCta: ContactCtaBanner = { title: "", description: "", buttonText: "", buttonLink: "", buttonUrl: "" };
export const defaultContactFaq: ContactFaqTeaser = { title: "", description: "", linkText: "", buttonLink: "", buttonText: "" };

export interface ContactHeroData {
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  badge: string;
  badgeEn?: string;
  typewriterWords: string[];
  typewriterWordsEn?: string[];
  backgroundImage: string;
}

export interface ContactMethod {
  id: string;
  icon: string;
  title: string;
  titleEn?: string;
  value: string;
  subValue?: string;
  subValueEn?: string;
  action?: "call" | "email" | "chat" | "link";
  actionText?: string;
  actionTextEn?: string;
  href: string;
  color: string;
  sub?: string;
  subEn?: string;
  enabled?: boolean;
}

export interface ContactFormConfig {
  title: string;
  titleEn?: string;
  subtitle: string;
  subtitleEn?: string;
  topics: string[];
  topicsEn?: string[];
  submitText: string;
  submitTextEn?: string;
  successMessage: string;
  successMessageEn?: string;
  successTitle: string;
  successTitleEn?: string;
  subjects: string[];
  subjectsEn?: string[];
  fields: {
    name: { label: string; placeholder: string; required: boolean };
    phone: { label: string; placeholder: string; required: boolean };
    email: { label: string; placeholder: string; required: boolean };
    subject: { label: string; required: boolean };
    message: { label: string; placeholder: string; required: boolean; maxLength: number };
  };
}

export interface ContactMapConfig {
  title: string;
  titleEn?: string;
  address: string;
  addressEn?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  embedUrl: string;
  height: number;
  mapsLink: string;
  mapsLinkText: string;
  mapsLinkTextEn?: string;
}

export interface ContactWorkingHoursItem {
  id: string;
  days: string;
  daysEn?: string;
  time: string;
  timeEn?: string;
  isOpen: boolean;
}
export type ContactWorkingHours = ContactWorkingHoursItem[];

export interface ContactSocialLink {
  id: string;
  platform: string;
  icon: string;
  url: string;
  enabled: boolean;
  label: string;
  labelEn?: string;
}

export interface ContactCtaBanner {
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  buttonText: string;
  buttonTextEn?: string;
  buttonLink: string;
  buttonUrl: string;
}

export interface ContactFaqTeaser {
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  linkText: string;
  linkTextEn?: string;
  buttonLink: string;
  buttonText: string;
  buttonTextEn?: string;
}

// -- Search Types --
export const defaultSearchHero: SearchHeroData = { title: "", description: "", placeholder: "", badge: "", subtitle: "" };
export const defaultSearchCTA: SearchCTAData = { title: "", description: "", buttonText: "", active: true, icon: "", subtitle: "", ctaPath: "", ctaLabel: "" };
export const defaultSearchResultsConfig: SearchResultsConfig = {
  showDoctors: true,
  showServices: true,
  showPackages: true,
  showBlog: true,
  doctorsPreviewCount: 3,
  servicesPreviewCount: 4,
  packagesPreviewCount: 4,
  blogPreviewCount: 3,
  showArticles: true,
  itemsPerPage: 10
};

export interface SearchHeroData {
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  placeholder: string;
  placeholderEn?: string;
  badge?: string;
  badgeEn?: string;
  subtitle?: string;
  subtitleEn?: string;
}

export interface PopularSearch {
  id: string;
  label: string;
  labelEn?: string;
  icon?: string;
  active?: boolean;
}

export interface QuickLink {
  id: string;
  label: string;
  labelEn?: string;
  url: string;
  icon: string;
  path?: string; // fallback
  colorStyle?: string; // fallback
  active?: boolean;
}

export interface SearchCTAData {
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  buttonText: string;
  buttonTextEn?: string;
  active?: boolean;
  icon?: string;
  subtitle?: string;
  subtitleEn?: string;
  ctaPath?: string;
  ctaLabel?: string;
  ctaLabelEn?: string;
}

export interface SearchResultsConfig {
  showDoctors: boolean;
  showServices: boolean;
  showPackages: boolean;
  showBlog: boolean;
  doctorsPreviewCount: number;
  servicesPreviewCount: number;
  packagesPreviewCount: number;
  blogPreviewCount: number;
  showArticles: boolean;
  itemsPerPage: number;
}

// -- Settings Types --
export const defaultClinicInfo: ClinicInfo = { name: "", logo: "", email: "", phone: "", address: "", mapEmbedUrl: "" };
export const defaultWorkingHours: WorkingHours = [
  { day: "الأحد", isOpen: true, from: "09:00", to: "21:00" },
  { day: "الإثنين", isOpen: true, from: "09:00", to: "21:00" },
  { day: "الثلاثاء", isOpen: true, from: "09:00", to: "21:00" },
  { day: "الأربعاء", isOpen: true, from: "09:00", to: "21:00" },
  { day: "الخميس", isOpen: true, from: "09:00", to: "21:00" },
  { day: "الجمعة", isOpen: false, from: "09:00", to: "21:00" },
  { day: "السبت", isOpen: false, from: "09:00", to: "21:00" }
];
export const defaultSocialMedia: SocialMedia = { twitter: "", instagram: "", linkedin: "", snapchat: "" };
export const defaultEmergencyContact: EmergencyContact = { phone: "", text: "" };
export const defaultSeoSettings: SeoSettings = { title: "عيادة الشفاء الطبية | رعاية صحية متكاملة", description: "عيادة الشفاء تقدم خدمات طبية متقدمة تشمل الجلدية، التجميل، العلاج الطبيعي والاستشارات العامة. احجز موعدك الآن.", keywords: "عيادة طبية، حجز موعد، طبيب جلدية، علاج بالخلايا، الرياض" };
export const defaultPixelSettings: PixelSettings = {
  fb_pixel: { id: "", enabled: false },
  ga4: { id: "", enabled: false },
  gtm: { id: "", enabled: false }
};
export const defaultSecuritySettings: SecuritySettings = {
  turnstile: { siteKey: "", secretKey: "", enabled: false }
};

export interface ClinicInfo {
  name: string;
  logo: string;
  email: string;
  phone: string;
  whatsapp?: string;
  address: string;
  mapEmbedUrl: string;
  terms?: string;
  termsEn?: string;
  privacy?: string;
  privacyEn?: string;
  termsSections?: Array<{ title: string; title_en?: string; content: string; content_en?: string }>;
  privacySections?: Array<{ title: string; title_en?: string; content: string; content_en?: string }>;
  nameEn?: string;
  tagline?: string;
  description?: string;
  addressEn?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  mapLat?: string | number;
  mapLng?: string | number;
  [key: string]: any;
}

export type WorkingHours = any;

export interface SocialMedia {
  twitter: string;
  instagram: string;
  linkedin: string;
  snapchat: string;
  [key: string]: any;
}

export interface EmergencyContact {
  phone: string;
  text: string;
  enabled?: boolean;
  note?: string;
  [key: string]: any;
}

export interface SeoSettings {
  title: string;
  description: string;
  keywords: string;
  [key: string]: any;
}

export interface PixelItem {
  id: string;
  enabled: boolean;
}

export interface PixelSettings {
  fb_pixel: PixelItem;
  ga4: PixelItem;
  gtm: PixelItem;
  [key: string]: any;
}

export interface SecuritySettings {
  turnstile: {
    siteKey: string;
    secretKey: string;
    enabled: boolean;
  };
  [key: string]: any;
}
